/**
 * Shift model — types for the staff clock screen and the mapping from the
 * backend `GET /schedule/shifts/today` response.
 *
 * A staff member's work day is grouped server-side into shifts (contiguous
 * EMPLOYEEEVENT records, split on long gaps) and paired with clock punches.
 * `ShiftService` on the backend owns that logic; this module just adapts
 * its JSON (ISO strings) into the `Shift` shape the screen renders (Dates).
 */

import { parseISO, isValid } from 'date-fns';

export type ShiftState = 'DONE' | 'LIVE' | 'UPCOMING' | 'MISSED';

export interface ShiftPunch {
  id: number;
  start: Date;
  end: Date | null; // null ⇒ open (still clocked in)
  minutes: number;
  approved: boolean;
}

/** A scheduled event within a shift (EMPLOYEEEVENT). */
export interface ShiftEvent {
  id: number;
  name?: string;
  type?: string;
  start?: string;
  end?: string;
  dept?: string;
  status?: string;
}

export interface Shift {
  key: string;
  /** 1-based number among scheduled shifts; 0 for an unscheduled clock-in. */
  number: number;
  label: string;
  scheduled: boolean;
  scheduledStart: Date;
  scheduledEnd: Date;
  scheduledMinutes: number;
  state: ShiftState;
  workedMinutes: number;
  /** Start of the currently-open punch, when state is LIVE. */
  liveSince: Date | null;
  events: ShiftEvent[];
  punches: ShiftPunch[];
}

// --- API response shapes ---------------------------------------------------

interface ShiftPunchDto {
  id: number;
  start: string;
  end: string | null;
  minutes: number;
  approved: boolean;
}

interface ShiftDto {
  number: number;
  label: string;
  scheduled: boolean;
  scheduledStart: string;
  scheduledEnd: string;
  scheduledMinutes: number;
  state: ShiftState;
  workedMinutes: number;
  liveSince: string | null;
  events: ShiftEvent[];
  punches: ShiftPunchDto[];
}

export interface ShiftsResponse {
  employeeId: number;
  employeeName: string;
  date: string;
  shifts: ShiftDto[];
}

const toDate = (iso: string | null | undefined): Date | null => {
  if (!iso) {
    return null;
  }
  const d = parseISO(iso);
  return isValid(d) ? d : null;
};

/** Adapt a `ShiftsResponse` into the `Shift[]` the screen renders. */
export function mapShifts(res: ShiftsResponse | null | undefined): Shift[] {
  if (!res || !res.shifts) {
    return [];
  }
  return res.shifts.map((s, i) => ({
    key: `shift-${i}`,
    number: s.number,
    label: s.label,
    scheduled: s.scheduled,
    scheduledStart: toDate(s.scheduledStart) ?? new Date(),
    scheduledEnd: toDate(s.scheduledEnd) ?? new Date(),
    scheduledMinutes: s.scheduledMinutes,
    state: s.state,
    workedMinutes: s.workedMinutes,
    liveSince: toDate(s.liveSince),
    events: s.events ?? [],
    punches: (s.punches ?? []).map((p) => ({
      id: p.id,
      start: toDate(p.start) ?? new Date(),
      end: toDate(p.end),
      minutes: p.minutes,
      approved: p.approved,
    })),
  }));
}

/** Key of the next shift a not-clocked-in employee would clock into. */
/**
 * Key of the shift a not-clocked-in employee would clock into: a shift they
 * already clocked out of while its scheduled window is still open (clocking
 * back in after a break), else the next upcoming shift.
 */
export function nextClockInShiftKey(shifts: Shift[], now: Date = new Date()): string | null {
  const resume = shifts.find((s) => s.state === 'DONE' && s.scheduled && now < s.scheduledEnd);
  if (resume) {
    return resume.key;
  }
  const next = shifts.find((s) => s.state === 'UPCOMING');
  return next ? next.key : null;
}
